import type {TrackManager} from '@geoblocks/edittrack';
import type {HTMLTemplateResult} from 'lit';
import {html} from 'lit';
import {
  compareFilters,
  copyTour,
  DEFAULT_BIKE_SPEED,
  deleteTour,
  exportTourGps,
  fetchFilters,
  fetchTourAndPois,
  getEmptyPOI,
  saveOrUpdateFilters,
  savePois,
} from '../apis/api-tours.ts';
import type {
  TourOwnProperties,
  TourPrefs,
  DetailedRecordedTour,
  DetailedTour,
  InternalPOIDetails,
  POIBackendFeature,
  RecordedTourMeta,
  TourFilter,
  TourMeta,
  TourType,
  HttpError,
} from '../apis/api-tours.ts';
import {getMainMap} from '../map/creation.ts';
import {adaptPoiFeatures, downloadGpx, unselectPois} from '../map/helpers.ts';
import {fromEPSG4326} from '../map/proj.ts';
import {getTrackManager} from '../map/track.ts';
import type {POIMeta} from '../stores/store-main.ts';
import {
  season$,
  modal$,
  bottomSheet$,
  userStatus$,
} from '../stores/store-main.ts';
import {shareTour$, resetShareTour} from '../stores/store-share-tour.ts';
import {PageElement} from './PageElement.ts';
import {property, query, state} from 'lit/decorators.js';
import type {Map, Feature} from 'ol';
import type {ElementMapWithContent} from '../elements/maps/map-element-with-content.ts';
import type {ElementMap} from '../elements/maps/map-element-map.ts';
import type {UserSettings} from '../apis/api-users.ts';
import {Subscription} from 'rxjs';
import {user$, userSettings$} from '../stores/store-user.ts';
import {edittrackMode$, map$, visibleLayers$} from '../stores/store-map.ts';
import {plusMenu$, setPlusMenuManager} from '../stores/store-plus-menu.ts';
import {scrollToTopSmooth} from '../helpers/dom.ts';
import type {Point, MultiLineString, LineString} from 'ol/geom.js';
import '../elements/element-poi-edit.ts';
import {
  activeTrackId$,
  activeTrackPoisCount$,
  currentlyActiveTourBikeSpeed$,
  currentlyActiveTourHikeSpeed$,
  currentlyActiveTourMountainBikeSpeed$,
  currentlyActiveTourSnowshoeSpeed$,
  currentlyActiveTourType$,
  currentlyActiveTourWinterhikeSpeed$,
  determineSpeed,
  gpxExportInProgress$,
  tourStyleData$,
  tourSettingsShown$,
} from '../stores/store-tours.ts';
import {Task} from '@lit/task';
import {
  createFiltersUpdate,
  createInternalPoisUpdate,
  mapToPoisUpdate,
} from '../elements/maps/get-save-tour-pois-helper.ts';
import {updateRecordedTourMeta} from '../meta/meta-recordedtrack.ts';
import {updateTourMeta} from '../meta/meta-tour.ts';
import {Router} from '@vaadin/router';
import {createToursURL, createTourURL} from '../urlHandling/urlCreate.ts';
import {getLanguageCode} from '../i18n.ts';
import {USER_ROLE} from '../helpers/helper-users.ts';
import {handleError} from '../helpers/errorHandling.ts';
import {msg} from '@lit/localize';
import {sendAnalytics} from '../analytics.ts';
import {GA_MEASUREMENT_ID} from '../envs.ts';
import {isPOIOverlay} from '../helpers/constants.ts';
import {isSummerLayer, tourTimeCodeToLayer} from '../layers/layerHelpers.ts';
import type {SeasonCode} from '../layers/layerTypes.ts';
import type {
  ElementTourSettings,
  TourConfigData,
} from '../elements/element-tour-settings.ts';

export function featureToPoiDetails(f: Feature<Point>): InternalPOIDetails {
  const meta = f.get('meta') as POIMeta;
  const poi: InternalPOIDetails = Object.assign(
    {
      id: f.getId() as number,
      geometry: f.getGeometry().getCoordinates(),
      index: f.get('index'), // related to position to start control point
    },
    meta,
  );
  return poi;
}

export async function undoMany(
  manager: TrackManager<POIMeta>,
  targetPosition: number,
) {
  const position = manager.historyPosition;
  for (let i = position; i > targetPosition; i--) {
    await manager.undo();
  }
}

export function backendToPoiDetails(b: POIBackendFeature): InternalPOIDetails {
  const p = b.properties;
  return {
    index: -1, // Will be recomputed by edittrack
    description: p.description,
    title: p.title,
    photo: p.photo,
    id: b.id as number, // FIXME: would it be great to enforce number without a cast
    geometry: fromEPSG4326(b.geometry.coordinates),
  };
}

export function renderPoiEditTemplate(
  poi: InternalPOIDetails,
  pref: TourPrefs,
  finisher: (evt: POIMeta) => void,
): HTMLTemplateResult {
  // We get here for 2 cases:
  // click to draw a new POI (then the fake POI is empty);
  // click to edit an existing POI (then the fake POI has some data)
  // in both cases it should provide an appropriate finisher.

  return html`<element-poi-edit
    .poi=${poi}
    .pref=${pref}
    @cancelEditPoi="${() => {
      cancelPoiEditing(finisher);
      bottomSheet$.next(undefined);
    }}"
    @confirmEditPoi="${(evt: CustomEvent<POIMeta>) => {
      confirmPoiEditing(evt, finisher, poi);
      bottomSheet$.next(undefined);
    }}"
  ></element-poi-edit>`;
}

function finishPoiEditing(
  evt: CustomEvent<POIMeta> | null,
  poi: InternalPOIDetails | null,
  finisher: (meta?: POIMeta, poi?: InternalPOIDetails) => void,
) {
  getTrackManager().mode = 'edit';
  getTrackManager().submode = '';
  unselectPois(getMainMap());
  finisher(evt?.detail, poi);
}

export function cancelPoiEditing(finisher?: (meta: POIMeta) => void) {
  finishPoiEditing(null, null, finisher);
}

export function confirmPoiEditing(
  evt: CustomEvent<POIMeta>,
  finisher: (meta: POIMeta, poi: InternalPOIDetails) => void,
  poi: InternalPOIDetails,
) {
  finishPoiEditing(evt, poi, finisher);
}

export abstract class AbstractPageMapTour extends PageElement {
  @property({type: Object}) scrollContainer: HTMLElement;
  @property({type: Number}) tourId: number | undefined;
  @property({type: String}) type: TourType;
  @property({type: Boolean}) fitMap = true;
  @property({type: Number}) activePoiId: number = undefined;
  @state() protected loggedIn = false;
  @state() protected invalidRole = false;
  @state() protected notOwner = false;
  @state() protected generalError = false;
  @state() protected adminMessageShown = false;
  @state() protected mapOverlayOpen = false;

  @state() protected editedTourFilter: TourFilter;
  @state() protected editedTourMeta: TourMeta | RecordedTourMeta; // note that RecordedTourMeta is not editable
  @state() protected editedTourProfile: number[][][];

  // This is necessary for showing the list of POIS in the content page
  @state() protected editedTourPois: InternalPOIDetails[];
  /**
   * The features converted as OL objets
   */
  @state() protected editedTourPoiFeatures: Feature<Point>[] | undefined;

  private _editedTourOwnProperties: TourOwnProperties;
  @state()
  set editedTourOwnProperties(top: TourOwnProperties) {
    this._editedTourOwnProperties = top;
    currentlyActiveTourType$.next(top?.type);
  }
  get editedTourOwnProperties(): TourOwnProperties {
    return this._editedTourOwnProperties;
  }

  @state() tourSettingsShown = false;

  /**
   * @readonly
   */
  private _editedTourPref: TourPrefs;
  @state()
  set editedTourPref(tp: TourPrefs) {
    this._editedTourPref = tp;
    tourStyleData$.next({
      colorRgb: tp.colorRgb,
      opacity: tp.opacity,
    });
    currentlyActiveTourBikeSpeed$.next(tp?.bikespeed);
    currentlyActiveTourHikeSpeed$.next(tp?.hikespeed);
    currentlyActiveTourWinterhikeSpeed$.next(tp?.winterhikespeed);
    currentlyActiveTourSnowshoeSpeed$.next(tp?.snowshoespeed);
    currentlyActiveTourMountainBikeSpeed$.next(tp?.mountainbikespeed);
  }
  get editedTourPref(): TourPrefs {
    return this._editedTourPref;
  }

  @state() protected mapInitialized: boolean;
  @state() protected isTourPublic: boolean = false;

  /**
   * The last retrieved tour.
   */
  protected backendTour?: DetailedTour | DetailedRecordedTour;

  protected backendFilters: TourFilter[];
  protected editedFilters: TourFilter[];

  // @ts-ignore TS6133
  private _fetchFiltersTask = new Task(this, {
    args: (): [boolean, boolean] => [
      !!this.backendFilters,
      this.mode !== 'edit',
    ],
    task: async ([backendSet, notEditMode]) => {
      if (backendSet || notEditMode) {
        return;
      }
      await this.fetchFilters();
    },
  });

  // @ts-ignore TS6133
  private _setTourSettingsTask = new Task(this, {
    args: (): [boolean, boolean, boolean] => [
      !this.editedFilters,
      !this.editedTourPref,
      !this.tourSettingsEl,
    ],
    task: ([noFiltersSet, noEditedTourPrefSet, noTourSettingsElSet]) => {
      // If not in new tour, without filters or user-settings
      if (noFiltersSet || noEditedTourPrefSet || noTourSettingsElSet) {
        return;
      }
      const config: TourConfigData = {
        bicycleSpeed: this.editedTourPref.bikespeed,
        hikeSpeed: this.editedTourPref.hikespeed,
        winterhikeSpeed: this.editedTourPref.winterhikespeed,
        snowshoeSpeed: this.editedTourPref.snowshoespeed,
        mountainBikeSpeed: this.editedTourPref.mountainbikespeed,
        color: this.editedTourPref.color,
        colorRgb: this.editedTourPref.colorRgb,
        name: this.editedTourOwnProperties.name,
        opacity: this.editedTourPref.opacity,
        timetype: this.editedTourPref.timetype,
        userdate: this.editedTourOwnProperties.userdate,
        filterId: this.editedTourFilter?.id,
        filters: this.editedFilters,
      };
      this.tourSettingsEl.config = config;
    },
  });

  fetchTourTask = new Task(this, {
    args: (): [boolean, boolean] => [!!this.tourId, !!this.backendTour],
    task: async ([tourIdSet, backendTourSet]) => {
      if (backendTourSet || !tourIdSet) {
        return;
      }
      this.notOwner = false;
      try {
        const result = await fetchTourAndPois(this.tourId, this.type);
        if (!result.tour) {
          return;
        }
        const tour = result.tour;

        tour.pois = result.pois.features.map((p) => {
          const poi = backendToPoiDetails(p);
          poi.photoValidity = 'valid';
          return poi;
        });

        this.backendTour = tour;
        const tourPoiFeatures = adaptPoiFeatures(
          result.pois,
          this.tourId,
          this.type === 'recordedtrack' ? 'recordedtrack' : 'normal',
        );
        this.isTourPublic = tour.properties.public;
        this.notOwner = !tour.properties.isOwner;
        this.populateEditedTourFromBackendTour(tourPoiFeatures);

        if (this.activePoiId) {
          const featureIndex = this.editedTourPoiFeatures.findIndex(
            (f) => f.getId() === this.activePoiId,
          );

          if (featureIndex !== -1) {
            const feature = this.editedTourPoiFeatures[featureIndex];
            feature.set('index', featureIndex);
            this.openPoi(feature);
          }
        }
        this.activePoiId = undefined;

        // Tour
        if (this.type === 'recordedtrack') {
          updateRecordedTourMeta(this.backendTour.properties.name, document);
        } else {
          updateTourMeta(this.backendTour.properties.name, document);
        }
      } catch (e: any) {
        updateTourMeta(this.tourId.toString(), document);
        if (e.status === 401 || e.status === 403) {
          this.notOwner = true;
        } else {
          console.error(e);
          this.generalError = true;
        }
      }
      sendAnalytics('config', GA_MEASUREMENT_ID, {content_group: 'page-tour'});
      sendAnalytics('event', 'page_view', {page_category: 'tour'});
      this.setShareTourState();
    },
  });

  @state() protected poiDrawModeEarlyCancel?: () => void = undefined;
  @state() protected mode: 'edit' | '' = '';
  @query('element-map') protected elementMap: ElementMap;
  @query('map-element-with-content')
  elementMapWithContent: ElementMapWithContent;
  @query('element-tour-settings') tourSettingsEl!: ElementTourSettings;
  protected map: Map | undefined;
  @state()
  protected userSettings: UserSettings;
  protected subscriptions: Subscription;

  protected manager = getTrackManager();

  // Needs to be defined by
  protected onTrackChangedListener: () => void;

  constructor() {
    super();
    setPlusMenuManager(this.manager);
  }

  override connectedCallback(): void {
    this.subscriptions = new Subscription();
    this.subscriptions.add(
      user$.subscribe((user) => {
        this.loggedIn =
          user !== null && userStatus$.getValue() !== USER_ROLE.noRole;
        this.invalidRole = ![
          USER_ROLE.subscribedUser,
          USER_ROLE.subscriptionPending,
          USER_ROLE.notLogged,
        ].includes(userStatus$.getValue());
      }),
    );
    this.subscriptions.add(
      bottomSheet$.subscribe((options) => {
        if (this.map && !options) {
          unselectPois(this.map);
        }
      }),
    );
    this.subscriptions.add(
      shareTour$.subscribe((shareTour) => {
        if (this.editedTourOwnProperties) {
          // editedTourOwnProperties used to save tour properties
          this.editedTourOwnProperties.public = shareTour.isTourPublic;
          this.isTourPublic = shareTour.isTourPublic;
        }
      }),
    );
    this.subscriptions.add(
      userSettings$.subscribe((settings: UserSettings) => {
        this.userSettings = settings;
      }),
    );
    this.subscriptions.add(
      map$.subscribe((map) => {
        this.map = map;
      }),
    );
    this.subscriptions.add(
      edittrackMode$.subscribe((mode) => {
        this.mode = mode;
        this.manager.mode = mode;
      }),
    );
    this.subscriptions.add(
      modal$.subscribe((options) => {
        if (!options && this.mapOverlayOpen) {
          window.history.replaceState(
            {},
            '',
            `${createTourURL(activeTrackId$.value, this.type, getLanguageCode())}`,
          );
        } else if (isPOIOverlay(options)) {
          this.mapOverlayOpen = true;
        }
      }),
    );
    this.subscriptions.add(
      tourSettingsShown$.subscribe((value) => {
        this.tourSettingsShown = value;
      }),
    );
    this.manager.addTrackChangeEventListener(this.onTrackChangedListener);
    const visibleLayers = visibleLayers$.getValue();
    visibleLayers$.next(
      visibleLayers.includes('trackLayer')
        ? visibleLayers
        : [...visibleLayers, 'trackLayer'],
    );
    this.setShareTourState();
    super.connectedCallback();
  }

  override disconnectedCallback() {
    sendAnalytics('config', GA_MEASUREMENT_ID, {content_group: ''});
    edittrackMode$.next('');
    resetShareTour();
    this.manager.removeTrackChangeEventListener(this.onTrackChangedListener);
    this.subscriptions.unsubscribe();
    super.disconnectedCallback();
  }

  abstract getSegments(): readonly Feature<LineString | MultiLineString>[];

  async downloadGPX(_event: CustomEvent) {
    gpxExportInProgress$.next(true);
    await downloadGpx(
      () =>
        exportTourGps(this.backendTour.id, this.backendTour.properties.type),
      this.backendTour?.properties.name,
      this.dispatchEvent.bind(this),
    );
    gpxExportInProgress$.next(false);
  }

  openPoi(feature: Feature<Point>) {
    feature.set('subtype', 'selected');
    const meta = feature.get('meta') as POIMeta;
    const fakePOI: InternalPOIDetails = {
      ...getEmptyPOI(),
      ...meta,
      index: feature.get('index'),
    };
    if (this.mode === 'edit') {
      feature.set('sketchHitGeometry', undefined);
      this.manager.mode = '';
      bottomSheet$.next({
        data: {state: 'open', showCloseBtn: true},
        guardElementRender: true,
        guardDependencies: [...Object.values(fakePOI)],
        element: renderPoiEditTemplate(
          fakePOI,
          this.editedTourPref,
          (meta: POIMeta) => {
            feature.unset('subtype');
            if (meta) {
              feature.set('meta', meta);
              getTrackManager().addManualHistoryEntry();
            }
          },
        ),
      });
    } else {
      bottomSheet$.next({
        data: {state: 'half', showCloseBtn: true},
        guardElementRender: true,
        guardDependencies: [...Object.values(fakePOI)],
        element: html`<element-poi-view
          .poi=${fakePOI}
          .pref=${this.editedTourPref}
        ></element-poi-view>`,
      });
    }
  }

  showPoiOnMap(poi: InternalPOIDetails) {
    if (!this.map) return;
    scrollToTopSmooth();
    const feature = this.editedTourPoiFeatures[poi.index];
    if (!feature) {
      console.warn('POI feature not found.');
      return;
    }
    this.map
      .getView()
      .fit(feature.getGeometry(), {padding: [10, 10, 10, 10], duration: 500});
  }

  addActivityLayer() {
    if (
      this.backendTour.properties.type === 'normal' &&
      this.editedTourPref.timetype !== 'no'
    ) {
      const layer = tourTimeCodeToLayer[this.editedTourPref.timetype];
      const visibleLayers = visibleLayers$.value;
      if (!visibleLayers.includes(layer)) {
        const season: SeasonCode = isSummerLayer(layer) ? 'summer' : 'winter';
        if (season$.value !== season) season$.next(season);
        visibleLayers$.next([...visibleLayers$.value, layer]);
      }
    }
  }

  populateEditedTourFromBackendTour(tourPoiFeatures: Feature<Point>[]) {
    this.editedTourPoiFeatures = tourPoiFeatures;
    const p = structuredClone(this.backendTour.properties);
    this.editedTourFilter = p.filter;
    this.editedTourMeta = p.meta;
    this.editedTourPref = p.pref;
    this.tourId = this.backendTour.id;

    // FIXME: resolving the bike speed and storing it immediately in the tour pref makes it fixed
    // If it is really what the customer wants, we would better completly remove the complex observable chain
    // and simply resolve the value:
    // - when the tour is fetched (since historically this value was not fixed);
    // - when a new tour is created.
    this.editedTourPref.bikespeed = determineSpeed(
      DEFAULT_BIKE_SPEED,
      this.userSettings?.bicycleSpeed,
      this.editedTourPref.bikespeed,
    );

    this.editedTourOwnProperties = {
      name: p.name,
      public: p.public,
      type: p.type,
      createdAt: p.createdAt,
      modifiedAt: p.modifiedAt,
      userdate: p.userdate,
    };

    activeTrackId$.next(this.tourId);
    activeTrackPoisCount$.next(tourPoiFeatures.length);

    if (this.tourId) {
      this.addActivityLayer();
    }

    // FIXME: we should probably restore the geom in manager
    // and maybe recompute the profile too
  }

  protected updatePlusMenu(
    controlPointsLength: number,
    poiFeaturesLength: number,
  ) {
    plusMenu$.next({
      poiBtnDisabled: controlPointsLength < 2 || poiFeaturesLength > 99,
      saveBtnDisabled: controlPointsLength < 2,
      undoBtnDisabled:
        (this.backendTour?.id && this.manager.historyPosition < 1) || // Existing tour
        this.manager.historyPosition < 0, // New tour
      redoBtnDisabled:
        this.manager.historyPosition === this.manager.historySize - 1,
    });
  }

  protected async saveFilters() {
    const filtersUpdate = createFiltersUpdate(
      this.editedFilters,
      this.backendFilters,
    );
    try {
      const result = await saveOrUpdateFilters(filtersUpdate);
      filtersUpdate.to_create.forEach((filter, index) => {
        const newId = result.created[index];

        // Reassign correct selected filter id if needed
        if (this.editedTourFilter?.id === filter.id) {
          this.editedTourFilter.id = newId;
        }

        // Reassign correct filters id in the filter list
        this.editedFilters.map((f) => {
          if (f.id === filter.id) {
            f.id = newId;
          }
          return f;
        });
        // Copy it to the backend reference filters
        this.backendFilters = structuredClone(this.editedFilters);
      });
    } catch (e) {
      handleError(e as HttpError, this.dispatchEvent.bind(this));
    }
  }

  protected async savePOIs(tourId: number) {
    // FIXME: we should detect if there is nothing to do and early exit
    const internalPoiUpdate = createInternalPoisUpdate(
      this.editedTourPois,
      this.backendTour?.pois || [],
    );
    const poisUpdate = mapToPoisUpdate(internalPoiUpdate);
    console.assert(
      internalPoiUpdate.toCreate.length ===
        poisUpdate.to_create.features.length,
    );
    const pois = await savePois(tourId, poisUpdate);
    console.assert(
      poisUpdate.to_create.features.length === pois.created.length,
    );
    internalPoiUpdate.toCreate.forEach((ipu, arrayIdx) => {
      // Assign allocated POI id to the current tour pois
      // This works because the arrayIdx is the same in:
      // - internalPoiUpdate.toCreated;
      // - poisUpdate.to_create;
      // - result.pois.created.
      const newId = pois.created[arrayIdx];
      ipu.id = newId;
    });

    this.editedTourPois = this.editedTourPois.map((poi) => {
      return {...poi, photoValidity: 'valid'};
    });
  }

  protected async deleteNormalOrRecordedTour() {
    try {
      await deleteTour(this.tourId, this.type);
      this.elementMap.clearTrackLayer();
      Router.go(`${createToursURL(getLanguageCode())}`);
    } catch (e) {
      handleError(e as HttpError, this.dispatchEvent.bind(this));
    }
  }

  protected async importNormalOrRecordedTour() {
    try {
      const response = await copyTour(this.tourId, this.type);
      if (!response.newly_imported) {
        this.dispatchEvent(
          new CustomEvent('alertnotif', {
            bubbles: true,
            detail: {
              type: 'information',
              message:
                this.type === 'normal'
                  ? msg(
                      'The tour has not been imported into your tour list because it already exists.',
                    )
                  : msg(
                      'The recorded track has not been imported into your tour list because it already exists.',
                    ),
            },
          }),
        );
        return;
      }
      Router.go(`${createTourURL(response.id, this.type, getLanguageCode())}`);
      this.dispatchEvent(
        new CustomEvent('alertnotif', {
          bubbles: true,
          detail: {
            type: 'success',
            message:
              this.type === 'normal'
                ? msg('The tour has been successfully imported.')
                : msg('The recorded track has been successfully imported.'),
          },
        }),
      );
    } catch (e) {
      handleError(e as HttpError, this.dispatchEvent.bind(this));
    }
  }

  protected get menuContext() {
    if (this.tourId && !this.notOwner) {
      return 'view';
    } else if (this.tourId && this.notOwner) {
      return 'shared-view';
    } else {
      return 'create';
    }
  }

  protected setShareTourState() {
    const shareTourState = {
      isTourOwner: this.tourId && !this.notOwner,
      isTourPublic: this.isTourPublic,
      isTourPage: true,
    };
    shareTour$.next(shareTourState);
  }

  protected async fetchFilters() {
    try {
      this.backendFilters = await fetchFilters();
      this.editedFilters = structuredClone(this.backendFilters).sort(
        compareFilters,
      );
    } catch (e) {
      if (this.loggedIn) {
        handleError(e as HttpError, this.dispatchEvent.bind(this));
      }
    }
  }
}
